import React, {useCallback, useEffect, useMemo, useRef} from 'react';
import {Box, Stack} from '@mui/material';
import {Body, Label, Tooltip} from '@lightricks/react-design-system';
import {
  restoreUnifiedFeedCache,
  setFirstUnreadMessageId,
} from '@/utils/cache-updaters/conversations';
import extractFirstName from '@/utils/extractFirstName';
import translate from '@/utils/translate';
import {ACCEPTED_MESSAGE_FILE_TYPES} from '@/config/conversationAttachments';
import ChatInput from '@/components/chat-input';
import MarkdownFormattingHint from '@/components/markdown-formatting-hint';
import useMarkAllRead from '@/hooks/mutations/conversations/use-mark-all-read';
import useMarkUnread from '@/hooks/mutations/conversations/use-mark-unread';
import useUnifiedMessages from '@/hooks/queries/use-unified-messages/useUnifiedMessages';
import useMessageAttachments from '@/hooks/use-message-attachments';
import useSendMessageWithAttachments from '@/hooks/use-send-message-with-attachments/useSendMessageWithAttachments';
import styles from '../CreatorSidePanel.module.scss';
import UnifiedFeedItemView from './UnifiedFeedItemView';

type MessagesTabProps = {
  conversationId: string;
  brandId: string;
  brandName: string;
  creatorName: string;
  campaignName?: string;
};

function MessagesTab({
  conversationId,
  brandId,
  brandName,
  creatorName,
  campaignName,
}: MessagesTabProps) {
  const [composedMessage, setComposedMessage] = React.useState('');
  // The campaign name is ellipsized in the cramped footer; only enable its
  // tooltip when it's actually clipped so we don't echo the visible text.
  const campaignNameRef = React.useRef<HTMLSpanElement>(null);
  const [isCampaignNameTruncated, setIsCampaignNameTruncated] =
    React.useState(false);
  React.useLayoutEffect(() => {
    const el = campaignNameRef.current;
    setIsCampaignNameTruncated(!!el && el.scrollWidth > el.clientWidth);
  }, [campaignName]);
  const {mutate: markAllRead} = useMarkAllRead();
  const {mutate: markUnread} = useMarkUnread({
    onMutate: ({messageId}) =>
      setFirstUnreadMessageId(conversationId, messageId),
    onError: (snapshot) => restoreUnifiedFeedCache(snapshot),
  });
  const {
    pendingAttachments,
    isUploading,
    handleFileChange,
    handleRemoveAttachment,
    sendAttachments,
    reset: resetAttachments,
  } = useMessageAttachments();
  const {
    items,
    fetchNextPage,
    firstUnreadMessageId,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    isSuccess,
    isError,
  } = useUnifiedMessages(conversationId);
  const {
    handleSend: sendMessageWithAttachments,
    isSending,
    pendingAttachmentMessageIds,
    scrollToBottomAfterSendRef,
  } = useSendMessageWithAttachments({
    conversationId,
    brandId,
    pendingAttachments,
    sendAttachments,
  });
  const bottomRef = useRef<HTMLDivElement>(null);
  const topSentinelRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const hasMarkedReadRef = useRef(false);

  const reversedItems = useMemo(() => [...items].reverse(), [items]);

  const newMessagesDividerIndex = useMemo(() => {
    if (!firstUnreadMessageId) return -1;
    return reversedItems.findIndex((item) => {
      if (item.kind === 'message' || item.kind === 'negotiation_snapshot') {
        return item.data.id === firstUnreadMessageId;
      }
      if (item.kind === 'version_thread') {
        return item.data.messages.some((m) => m.id === firstUnreadMessageId);
      }
      return false;
    });
  }, [firstUnreadMessageId, reversedItems]);

  useEffect(
    function markConversationRead() {
      if (isSuccess && !hasMarkedReadRef.current) {
        hasMarkedReadRef.current = true;
        markAllRead({conversationId});
      }
    },
    [conversationId, isSuccess, markAllRead]
  );

  useEffect(
    function resetMarkReadOnConversationChange() {
      hasMarkedReadRef.current = false;
    },
    [conversationId]
  );

  useEffect(
    function resetAttachmentsOnConversationChange() {
      resetAttachments();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [conversationId]
  );

  useEffect(
    function scrollToBottomOnInit() {
      if (!isLoading) {
        bottomRef.current?.scrollIntoView({behavior: 'instant'});
      }
    },
    [conversationId, isLoading]
  );

  useEffect(
    function scrollToBottomAfterSend() {
      if (scrollToBottomAfterSendRef.current) {
        scrollToBottomAfterSendRef.current = false;
        bottomRef.current?.scrollIntoView({behavior: 'smooth'});
      }
    },
    [items]
  );

  useEffect(
    function loadMoreOnScrollToTop() {
      const sentinel = topSentinelRef.current;
      const container = scrollContainerRef.current;
      if (!sentinel || !hasNextPage || isFetchingNextPage) {
        return () => {};
      }

      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            fetchNextPage();
          }
        },
        {root: container, threshold: 0}
      );
      observer.observe(sentinel);
      return () => {
        observer.disconnect();
      };
    },
    [fetchNextPage, hasNextPage, isFetchingNextPage]
  );

  const handleMarkUnread = useCallback(
    (messageId: string) => {
      markUnread({messageId});
    },
    [markUnread]
  );

  const handleSend = useCallback(() => {
    sendMessageWithAttachments(composedMessage, () => setComposedMessage(''));
  }, [composedMessage, sendMessageWithAttachments]);

  const creatorFirstName = extractFirstName(creatorName);

  return (
    <>
      <Box className={styles.tabContent} ref={scrollContainerRef}>
        <Stack className={styles.messagesThread}>
          <div ref={topSentinelRef} />
          {isError ? (
            <Body size="md" className={styles.emptyState}>
              {translate('errors.please-try-again-or-come-back-later')}
            </Body>
          ) : null}
          {!isLoading && !isError && items.length === 0 ? (
            <Body size="md" className={styles.emptyState}>
              {translate('start_conversation_with', {
                creatorName: creatorFirstName,
              })}
            </Body>
          ) : null}
          {reversedItems.map((item, index) => (
            <React.Fragment
              key={
                item.kind === 'version_thread'
                  ? item.data.versionId
                  : item.data.id
              }
            >
              {index === newMessagesDividerIndex && (
                <div className={styles.newMessagesDivider}>
                  <span className={styles.newMessagesDividerLabel}>
                    {translate('New Messages')}
                  </span>
                </div>
              )}
              <UnifiedFeedItemView
                item={item}
                conversationId={conversationId}
                isLoadingAttachments={
                  item.kind === 'message' &&
                  pendingAttachmentMessageIds.has(item.data.id)
                }
                onMarkUnread={handleMarkUnread}
              />
            </React.Fragment>
          ))}
          <div ref={bottomRef} />
        </Stack>
      </Box>

      <Stack className={styles.composeArea}>
        <ChatInput
          value={composedMessage}
          onChange={setComposedMessage}
          onSubmit={handleSend}
          placeholder={`${translate('message_creator_name', {creatorName: creatorFirstName})}...`}
          isSubmitting={isSending || isUploading}
          showEmoji
          attachments={{
            pending: pendingAttachments,
            isUploading,
            onFileChange: handleFileChange,
            onRemoveAttachment: handleRemoveAttachment,
            accept: ACCEPTED_MESSAGE_FILE_TYPES.join(','),
          }}
        />
        <div className={styles.composeFooter}>
          <div className={styles.composeMessagingAs}>
            <Body size="sm" component="span">
              {translate('Messaging as')}
            </Body>
            <Label size="sm">{brandName}</Label>
            {campaignName ? (
              <Tooltip
                title={campaignName}
                arrow
                placement="top"
                disableHoverListener={!isCampaignNameTruncated}
                disableFocusListener={!isCampaignNameTruncated}
                disableTouchListener={!isCampaignNameTruncated}
              >
                <Box
                  component="span"
                  ref={campaignNameRef}
                  className={styles.composeMessagingAsCampaign}
                >
                  <Body size="sm" component="span">
                    ({campaignName})
                  </Body>
                </Box>
              </Tooltip>
            ) : null}
          </div>
          <MarkdownFormattingHint variant="tooltip" />
        </div>
      </Stack>
    </>
  );
}

export default MessagesTab;
